import * as PIXI from 'pixi.js';
import { gsap } from 'gsap';
import PixiPlugin from 'gsap/PixiPlugin';


declare global {
  interface Window {
    particles: {
      [key: string]: any;
    };
  }
}

interface ParticleMultipleGraphic {
  pixiGraphic: PIXI.Graphics;
  origColor: number;
  origX: number;
  origY: number;
  positions: Array<{ color: number; x: number; y: number }>;
}

interface ParticleSingleGraphic {
  pixiGraphic: PIXI.Graphics;
  origColor: number;
  origX: number;
  origY: number;
  color: number;
  x: number;
  y: number;
}


class ParticlesAnimation {
  private particleGraphicListMultiple: { [key: string]: ParticleMultipleGraphic[] } = {};
  private particleGraphicListSingle: { [key: string]: ParticleSingleGraphic[] } = {};
  private currentAnimation = 0;

  constructor() {
    this.init();
  }

  private async init() {
    this.initializeLibraries();

    const particles = Array.from(
      document.querySelectorAll('[data-trigger^="particles"]')
    );
    for (let particle of particles) {
      // Get graph attributes
      const graphicName = particle.getAttribute('data-source') || 'graphic';
      const canvas = particle.querySelector('canvas') as HTMLCanvasElement;
      const canvasWidth = particle.getAttribute('data-width') || 500;
      const canvasHeight = particle.getAttribute('data-height') || 500;
      const animationType = particle.getAttribute('data-type') || "single";
      const backroundColor = particle.getAttribute('data-background') || '#000';
      const backroundAlpha =
        particle.getAttribute('data-backgroundalpha') || '1';
      const sizeMultiplier = particle.getAttribute('data-zoom') || '1';

      // Get graph coordinates from json file
      const particlesDatasMultiple: string[][][] =
        await this.fetchGraphicCoordinatesFromJson(graphicName);

      const particlesDatasSingle: string[][] =
        await this.fetchGraphicCoordinatesFromJson(graphicName);

      // Create a graphic object
      this.initializeGraphic(
        graphicName,
        canvas,
        +canvasWidth,
        +canvasHeight,
        particlesDatasMultiple,
        particlesDatasSingle,
        animationType,
        backroundColor,
        backroundAlpha,
        parseFloat(sizeMultiplier)
      );

      // Trigger animation when the graph enters the viewport
      const ob = new IntersectionObserver(
        (res) => {
          if (res[0].isIntersecting) {
            this.redrawGraphicMultiple(graphicName);
            this.redrawGraphicSingle(graphicName);
            ob.disconnect();
          }
        },
        { threshold: 0.8 }
      );
      ob.observe(particle);
    }
  }

  private initializeLibraries() {
    gsap.registerPlugin(PixiPlugin);
    PixiPlugin.registerPIXI(PIXI);
    PIXI.BaseTexture.defaultOptions.scaleMode = PIXI.SCALE_MODES.NEAREST;
  }

  private async initializeGraphic(
    graphicName: string,
    canvas: HTMLCanvasElement,
    canvasWidth: number,
    canvasHeight: number,
    particlesDatasMultiple: string[][][],
    particlesDatasSingle: string[][],
    animationType: string,
    backroundColor: string,
    backroundAlpha: string,
    sizeMultiplier: number
  ) {
    if (animationType == "multiple") {
      this.particleGraphicListMultiple[graphicName] = [];
      const app = new PIXI.Application({
        backgroundColor: this.getColor(backroundColor),
        backgroundAlpha: +backroundAlpha,
        width: canvasWidth * sizeMultiplier,
        height: canvasHeight * sizeMultiplier,
        view: canvas
      });
      // for (const particlesArray of particlesDatas) {
      for (const item of particlesDatasMultiple[this.currentAnimation]) {
        const x = +item[0] * sizeMultiplier;
        const y = +item[1] * sizeMultiplier;
        const color = this.getColor(item[2]);

        // Create a raw PIXI object
        const pixiGraphic = new PIXI.Graphics();
        app.stage.addChild(pixiGraphic);

        // Encapsulate the PIXI object in a particle object with both destructured and final position
        const particle: ParticleMultipleGraphic = {
          pixiGraphic,
          origColor: this.getColor('#ffffff'),
          origX: this.randomIntFromInterval(0, canvasWidth * sizeMultiplier),
          origY: this.randomIntFromInterval(0, canvasHeight * sizeMultiplier),
          positions: particlesDatasMultiple.map(forme => {
            const positionIndex = particlesDatasMultiple[this.currentAnimation].indexOf(item);
            const positionPoint = forme[positionIndex];
            const positionX = +positionPoint[0] * sizeMultiplier;
            const positionY = +positionPoint[1] * sizeMultiplier;
            const positionColor = this.getColor(positionPoint[2]);
            return ({
              color: positionColor,
              x: positionX,
              y: positionY,
            })
          }),
        };
        // opacity 0 if random > 0.1 (90%)
        if (Math.random() > 0.1) {
          particle.pixiGraphic.alpha = 0;
        }
        particle.pixiGraphic.beginFill(particle.origColor);
        particle.pixiGraphic.drawRect(particle.origX, particle.origY, 2, 2);

        // animate the particles
        const randomX = 150 * sizeMultiplier;
        const randomY = 150 * sizeMultiplier;
        gsap.to(particle.pixiGraphic, {
          x: `random(-${randomX}, ${randomX}, 1)`,
          y: `random(-${randomY}, ${randomY}, 1)`,
          duration: Math.random() * 20 + 10,
          repeat: -1,
          ease: 'none'
        });

        this.particleGraphicListMultiple[graphicName].push(particle);
        this.resetCanvas(canvas);
      }
      console.log(particlesDatasMultiple);
      console.log(this.particleGraphicListMultiple[graphicName]);
      //console.log(this.particleGraphicList);

      // this.particleGraphicList.sort((a, b) => 0.5 - Math.random());
      // }
      //console.log(this.currentAnimation);
    } else {
      this.particleGraphicListSingle[graphicName] = [];
      const app = new PIXI.Application({
        backgroundColor: this.getColor(backroundColor),
        backgroundAlpha: +backroundAlpha,
        width: canvasWidth * sizeMultiplier,
        height: canvasHeight * sizeMultiplier,
        view: canvas
      });
      // for (const particlesArray of particlesDatas) {
      for (const item in particlesDatasSingle) {
        const x = +particlesDatasSingle[item][0] * sizeMultiplier;
        const y = +particlesDatasSingle[item][1] * sizeMultiplier;
        const color = this.getColor(particlesDatasSingle[item][2]);

        // Create a raw PIXI object
        const pixiGraphic = new PIXI.Graphics();
        app.stage.addChild(pixiGraphic);

        // Encapsulate the PIXI object in a particle object with both destructured and final position
        const particle: ParticleSingleGraphic = {
          pixiGraphic,
          origColor: this.getColor('#ffffff'),
          origX: this.randomIntFromInterval(0, canvasWidth * sizeMultiplier),
          origY: this.randomIntFromInterval(0, canvasHeight * sizeMultiplier),
          // Final position
          color: color,
          x: x,
          y: y
        };

        particle.pixiGraphic.beginFill(particle.origColor);
        particle.pixiGraphic.drawRect(particle.origX, particle.origY, 2, 2);

        // animate the particles
        const randomX = 150 * sizeMultiplier;
        const randomY = 150 * sizeMultiplier;
        gsap.to(particle.pixiGraphic, {
          x: `random(-${randomX}, ${randomX}, 1)`,
          y: `random(-${randomY}, ${randomY}, 1)`,
          duration: Math.random() * 20 + 10,
          repeat: -1,
          ease: 'none'
        });

        this.particleGraphicListSingle[graphicName].push(particle);
        this.resetCanvas(canvas);
      }
      console.log(particlesDatasSingle);
      console.log(this.particleGraphicListSingle[graphicName]);
      //console.log(this.particleGraphicList);

      // this.particleGraphicList.sort((a, b) => 0.5 - Math.random());
      // }
      //console.log(this.currentAnimation);
    }
  }

  private resetCanvas(canvas) {
    window.onbeforeunload = () => {
      canvas.style.opacity = "0";
    };
  }

  private async fetchGraphicCoordinatesFromJson(name: string) {
    var domainUrl = window.location.protocol + '//' + window.location.hostname;

    return await fetch(
      `${domainUrl}/wp-content/themes/treez/dist/assets/images/${name}.json`
    )
      .then((response) => response.json())
      .catch((error) => {
        console.error('Erreur lors du chargement du fichier JSON:', error);
      });
  }

  public redrawGraphicMultiple(graphicName: string) {
    const particleGraphicListMultiple = this.particleGraphicListMultiple[graphicName];
    const mask = document.querySelector('.canvas__mask') as HTMLElement;
    mask.style.opacity = '0';

    for (const index in particleGraphicListMultiple) {
      var particleObject = particleGraphicListMultiple[index];
      var particlePixi = particleObject.pixiGraphic;
      gsap.killTweensOf(particlePixi);

      var x = particleObject.positions[this.currentAnimation].x - particleObject.origX;
      var y = particleObject.positions[this.currentAnimation].y - particleObject.origY;

      gsap.to(particlePixi, {
        pixi: { tint: particleObject.positions[this.currentAnimation].color },
        x: x,
        y: y,
        alpha: 1,
        duration: 2,
        ease: 'power4.inOut',
        delay: (0.004 * +index) / 7,
        onCompleteParams: [particlePixi, x, y],
        onComplete: function (e, u, v) {
          //   gsap.killTweensOf(e);
          //   gsap.to(e, { pixi : {scaleX : 2, scaleY : 2},
          //      duration: 3, repeat: -1, yoyo: true, ease: "none"
          // });
          // gsap.killTweensOf(canvas);
          // gsap.to(canvas, {rotation : 360,
          //    duration: 10, repeat: -1, ease: "none"
          // });
        }
      });
    }
    setTimeout(() => {
      this.reverseGraphic(graphicName);
    }, 3000)
  }

  public reverseGraphic(graphicName: string) {
    const particleGraphicListMultiple = this.particleGraphicListMultiple[graphicName];

    for (const index in particleGraphicListMultiple) {
      var particleObject = particleGraphicListMultiple[index];
      var particlePixi = particleObject.pixiGraphic;
      gsap.killTweensOf(particlePixi);

      var x = particleObject.origX
      var y = particleObject.origY
      var color = particleObject.origColor

      gsap.to(particlePixi, {
        pixi: { tint: color },
        x: x,
        y: y,
        alpha: 1,
        duration: 2,
        ease: 'power4.inOut',
        delay: (0.004 * +index) / 7,
        onComplete: (e, u, v) => {
        }
      });

    }
    setTimeout(() => {
      this.currentAnimation = (this.currentAnimation + 1) % particleGraphicListMultiple[0].positions.length;
      this.redrawGraphicMultiple(graphicName);
    }, 3000)
  }

  public redrawGraphicSingle(graphicName: string) {
    const particleGraphicListSingle = this.particleGraphicListSingle[graphicName];
    const mask = document.querySelector('.canvas__mask') as HTMLElement;
    mask.style.opacity = '0';

    for (const index in particleGraphicListSingle) {
      var particleObject = particleGraphicListSingle[index];
      var particlePixi = particleObject.pixiGraphic;
      gsap.killTweensOf(particlePixi);

      var x = particleObject.x - particleObject.origX;
      var y = particleObject.y - particleObject.origY;
      gsap.to(particlePixi, {
        pixi: { tint: particleObject.color },
        x: x,
        y: y,
        duration: 2,
        ease: 'power4.inOut',
        delay: (0.004 * +index) / 7,
        onCompleteParams: [particlePixi, x, y],
        onComplete: function (e, u, v) {
          //   gsap.killTweensOf(e);
          //   gsap.to(e, { pixi : {scaleX : 2, scaleY : 2},
          //      duration: 3, repeat: -1, yoyo: true, ease: "none"
          // });
          // gsap.killTweensOf(canvas);
          // gsap.to(canvas, {rotation : 360,
          //    duration: 10, repeat: -1, ease: "none"
          // });
        }
      });
    }
  }

  private randomIntFromInterval(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  private getColor(color: string) {
    return new PIXI.Color(color).toNumber();
  }
}

export let particlesAnimation = new ParticlesAnimation();
